import type { TabKey } from "../components/TabBar";

// Sprint-008 A1 — URL routing foundation. `wouter`'s default browser
// location hook returns the PATHNAME only (no query string — see
// `useSearch()`/`useSearchParams()` for that, unused here); every mapping
// below is pathname-only by design, so `?link=invalid` (still owned by
// `hooks/useTokenInvalidFlag.ts`, entirely independent of this router) is
// never in scope of these functions.
//
// `ResolvedView` is a superset of `TabKey`: the five bottom-tab keys plus
// "animals"/"projects", which are routed (task brief item #2 names them
// explicitly) but are NOT their own tab-bar entries — they stay reachable
// from the More menu, same as today, just via `navigate()` instead of
// `MoreView`'s local `subView` state. See `app.tsx`'s module docstring for
// the full "which views got routes" line this sprint drew.
//
// Sprint-008 A2 — "people" | "chores" | "settings" added. `app.tsx`'s own
// A1 docstring flagged all three as work it deliberately deferred to THIS
// epic: "the Admin People/Chores sub-tabs are EXACTLY the pattern epic A2
// already plans to promote to first-class sidebar entries" and "'Sign in &
// security' stays a local subview... on the same 'revisit if A2's sidebar
// promotes it to a first-class entry' reasoning." The desktop sidebar
// (design-desktop-phase-a.md §1.2) gives People, Chores, and "Your
// sign-in" their own first-class rows/URLs — each needs its own
// `ResolvedView` (not just a new path aliased onto "admin"/"more") so
// `app.tsx`'s canonical-URL-normalization effect doesn't immediately
// rewrite `/admin/people` back to `/admin`. `AdminView` reads `/admin/
// people` vs `/admin/chores` vs bare `/admin` itself (via `useLocation()`)
// to pick its subtab — see that component's own docstring.
//
// Sprint-008 A4 (design-desktop-phase-a.md §2) — "dashboard" added, the
// new desktop landing view (`/dashboard`, `views/DashboardView.tsx`).
// Everyone can reach it (no role gate — its own CONTENT is role-scoped,
// see that view's docstring), so — unlike people/chores/settings — it
// needs no `app.tsx` authorization guard, only the landing-path special
// case below.
// Sprint-009 B4 (design-phase-b.md §1) — "calendar" added. Everyone-visible
// (§1.1: placed beside Today/My list, no role gate on the ROUTE itself —
// same "authz lives in which hooks fire, not in which components render"
// posture `dashboard` already established; the calendar's own WRITE
// affordances are gated internally by `canHandOutWork`, see
// `views/CalendarScreen.tsx`), so — like `dashboard` — it needs no
// `app.tsx` authorization guard, only a `tabKeyForView` mapping below.
//
// Sprint-009 B4/B5 (design-phase-b.md §1) — "reports" and "activity"
// added. "reports" (`/reports`, admin+manager, `canHandOutWork`-gated —
// `app.tsx` guards this route the same way it already guards `/team`/
// `/projects`) is a standalone screen, same shape as `calendar`/`animals`/
// `projects` — reachable from the desktop sidebar AND a phone `More`
// landing row. "activity" (`/activity`, admin/api only) is routed to the
// TOP LEVEL (§1.1's own table — not nested under `/admin/`, matching this
// sprint's sibling top-level routes) but is RENDERED by `AdminView` as a
// third subtab alongside People/Chores (§1.2) — `app.tsx`'s admin-gate
// guard (the same one already covering `admin`/`people`/`chores`) extends
// to cover it too.
//
// Sprint-010 C4 (design-phase-c.md §1.1/§1.2) — "contacts" added.
// `canHandOutWork`-gated (admin OR manager, ADR-002 A6 decision 8.2),
// same shape/gate as `projects`/`reports` — reachable from the desktop
// sidebar (positioned directly after Projects) and a phone `More` landing
// row (positioned the same way).
//
// Sprint-014 C1 (config-page-ia-layout-2026-07-10.md §1) — "config" added.
// admin/api only (NOT manager), routed to the TOP LEVEL exactly like
// "activity" (not nested under `/admin/`) but RENDERED by `AdminView` as a
// fourth subtab alongside People/Chores/Activity — identical shape to how
// "activity" joined this file in sprint-009 B3.
export type ResolvedView =
  | TabKey
  | "animals"
  | "projects"
  | "people"
  | "chores"
  | "settings"
  | "dashboard"
  | "calendar"
  | "reports"
  | "activity"
  | "contacts"
  | "config";

export const VIEW_PATHS: Record<ResolvedView, string> = {
  today: "/today",
  mylist: "/my",
  team: "/team",
  admin: "/admin",
  more: "/more",
  animals: "/animals",
  projects: "/projects",
  people: "/admin/people",
  chores: "/admin/chores",
  settings: "/settings",
  dashboard: "/dashboard",
  calendar: "/calendar",
  reports: "/reports",
  activity: "/activity",
  contacts: "/contacts",
  config: "/config",
};

// Sprint-008 A4 — "/" is deliberately ABSENT from this table now (it used
// to hardcode "today"). §2's "'/' -> /dashboard at >=1024px, -> /today
// below" landing rule needs `isDesktop`, which this pure, viewport-
// unaware function has no access to — `app.tsx` (which DOES know the
// viewport) now resolves the "/" special case itself, in its own
// `resolvedView` fallback, the same place an unrecognized path already
// fell back to a role-appropriate default. See that file's docstring.
const PATH_TO_VIEW: Record<string, ResolvedView> = {
  "/today": "today",
  "/my": "mylist",
  "/team": "team",
  "/admin": "admin",
  "/more": "more",
  "/animals": "animals",
  "/projects": "projects",
  "/admin/people": "people",
  "/admin/chores": "chores",
  "/settings": "settings",
  "/dashboard": "dashboard",
  "/calendar": "calendar",
  "/reports": "reports",
  "/activity": "activity",
  "/contacts": "contacts",
  "/config": "config",
};

/** Maps a pathname to the view it names — `null` for "/" (see the table's
 * own docstring above), a stale bookmark, a typo, or any other path this
 * app doesn't recognize. The caller (`app.tsx`) treats `null` as "pick
 * this session's landing view" (Dashboard at desktop, Today on phone),
 * then normalizes the URL bar to the resolved view's canonical path via a
 * redirect effect. */
export function viewForPath(pathname: string): ResolvedView | null {
  return PATH_TO_VIEW[pathname] ?? null;
}

/** Collapses every routed-but-not-tabbed view onto the `TabBar` entry a
 * phone user would actually reach it through, for `TabBar`'s `active`
 * prop — none of `animals`/`projects`/`people`/`chores`/`settings`/
 * `dashboard` has a tab-bar entry of its own. `animals`/`projects`
 * collapse onto "more" (reachable from the More menu, unchanged since
 * A1); `people`/`chores` collapse onto "admin" (the admin's Admin tab, whose
 * own subtab strip picks between them on phone — see `AdminView.tsx`);
 * `settings` collapses onto "more" ("Your sign-in" is a More-menu row on
 * phone, unchanged — see `MoreView.tsx`); `dashboard` collapses onto
 * "today" (Dashboard is desktop's landing/"home" view — Today is its
 * closest phone equivalent, and the only way this mapping is ever
 * exercised at all is a phone session bookmarking/typing `/dashboard`
 * directly, since no phone tap ever routes there). This mapping only
 * matters if one of these routes is ever reached below the desktop
 * breakpoint (e.g. a bookmarked URL) — every EXISTING phone tap-through
 * still drives `TabBar` exactly as before, never via this function. */
export function tabKeyForView(view: ResolvedView): TabKey {
  // Sprint-009 B4 (design-phase-b.md §1.2) — "calendar" joins this group:
  // a standalone, everyone-visible screen with its own identity (same
  // shape as Animals/Projects), reachable from the phone More landing,
  // not a tab of its own. Sprint-009 B4 also adds "reports" here — same
  // reasoning, reachable from a `More` landing row (§1.2's own table).
  if (
    view === "animals" ||
    view === "projects" ||
    view === "settings" ||
    view === "calendar" ||
    view === "reports" ||
    view === "contacts"
  ) {
    return "more";
  }
  // Sprint-009 B3 (design-phase-b.md §1.2) — "activity" joins People/
  // Chores here: a third face of the same admin-only admin console, not a
  // standalone screen (see `AdminView.tsx`'s own docstring). Sprint-014 C1
  // — "config" joins as a fourth face, same reasoning.
  if (view === "people" || view === "chores" || view === "activity" || view === "config")
    return "admin";
  if (view === "dashboard") return "today";
  return view;
}
